// UC91-16, UC93-12, UC50-05 (CF-59): the movement a correction replaces, named the same way
// wherever a correction is made or shown: its report, the day that report was accepted and the
// channel it came by. One read per movement (`GET /stock/movements/:id`) under the key the
// reporting page already opens correction mode with, so the page, the dialog and the draft table
// share it. per docs/shape-ops-movement-correction-source.md §2 D1, D3, D4
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { formatDate, MetadataLine } from '@simsm/ui-shared';
import { getMovement, type MovementForCorrection } from './api';

export function useCorrectionTarget(id: string | undefined) {
  return useQuery({
    queryKey: ['stock-movement', id],
    queryFn: () => getMovement(id ?? ''),
    enabled: Boolean(id),
    retry: false,
    staleTime: Infinity,
  });
}

/** A report's channel as the screens name it, "Manual" or "API"; null for one not stated. */
export function movementSourceKey(source: string): string | null {
  if (source === 'MANUAL') return 'submission_detail.meta.source.manual';
  if (source === 'AUTOMATED') return 'submission_detail.meta.source.automated';
  return null;
}

type TargetFacts = Pick<MovementForCorrection, 'reportNumber' | 'confirmedAt' | 'source'>;

export function CorrectionTargetLine({ movement }: { movement: TargetFacts }): JSX.Element | null {
  const { t } = useTranslate();
  const sourceKey = movementSourceKey(movement.source);
  return (
    <MetadataLine
      wrap
      items={[
        { label: t('stock_reporting.correction_target.report'), value: movement.reportNumber },
        {
          label: t('stock_reporting.correction_target.accepted'),
          value: formatDate(movement.confirmedAt),
        },
        { label: t('submission_detail.meta.source'), value: sourceKey ? t(sourceKey) : '' },
      ]}
    />
  );
}

/** The draft table's mark of a correction row: what it corrects, once that is read. */
export function CorrectionReference({ movementId }: { movementId: string }): JSX.Element | null {
  const target = useCorrectionTarget(movementId);
  return target.data ? <CorrectionTargetLine movement={target.data} /> : null;
}
